import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type {
  StatsResponse,
  StatsBySource,
  StatsTerritorial,
  StatsByEstado,
  StatsPriceBucket,
} from '../lib/types';
import { ESTADO_NAMES } from '../lib/filters';
import { HealthDashboard } from './HealthDashboard';

interface Props {
  showProperties: boolean;
  setShowProperties: (v: boolean) => void;
  showHeatmap: boolean;
  setShowHeatmap: (v: boolean) => void;
  showSocio: boolean;
  setShowSocio: (v: boolean) => void;
  /** Properties que matchean los filtros activos (de /v1/facets). */
  matchedTotal: number | null;
}

export function Sidebar({ showProperties, setShowProperties, showHeatmap, setShowHeatmap, showSocio, setShowSocio, matchedTotal }: Props) {
  const [stats, setStats] = useState<StatsResponse | null>(null);
  // En el celular la barra lateral va plegada: abierta se comía el 40% de
  // la pantalla por encima del mapa.
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const ctrl = new AbortController();
    api.stats(ctrl.signal).then(setStats).catch(() => {});
    return () => ctrl.abort();
  }, []);

  // Inventario EN EL MERCADO (RFC-0028): lo que la fuente dejó de publicar
  // ya no cuenta. `properties` (todas las filas) queda para operación.
  const totalProps = stats?.counts.active_properties ?? stats?.counts.properties ?? 0;

  return (
    <aside className={`sidebar${mobileOpen ? ' mobile-open' : ''}`}>
      <button
        type="button"
        className="sidebar-mobile-toggle"
        aria-expanded={mobileOpen}
        onClick={() => setMobileOpen((v) => !v)}
      >
        Capas y estadísticas <span aria-hidden>{mobileOpen ? '▴' : '▾'}</span>
      </button>
      <section className="section">
        <div className="section-title">Capas</div>
        <label className="toggle">
          <input
            type="checkbox"
            checked={showProperties}
            onChange={(e) => setShowProperties(e.target.checked)}
          />
          <span>Propiedades distressed</span>
        </label>
        <label className="toggle">
          <input
            type="checkbox"
            checked={showHeatmap}
            onChange={(e) => setShowHeatmap(e.target.checked)}
          />
          <span>Heatmap vacancia (AGEB)</span>
        </label>
        <label className="toggle">
          <input
            type="checkbox"
            checked={showSocio}
            onChange={(e) => setShowSocio(e.target.checked)}
          />
          <span>Rezago social (AGEB)</span>
        </label>
      </section>

      <section className="section">
        <div className="section-title">Inventario</div>
        {stats ? (
          <div className="stats-grid">
            <Stat label="En el mercado" value={totalProps} />
            {/* RFC-0021 A1: FUENTES distintas activas (6), no filas de
                property_sources (14k) — "14,104 SOURCES" se leía como
                "14 mil fuentes". */}
            <Stat label="Fuentes activas" value={stats.activeSources ?? stats.bySource?.length ?? 0} />
            <Stat label="Estados" value={stats.coverage?.estados ?? 0} />
            <Stat label="Municipios" value={stats.coverage?.municipios ?? 0} />
            <Stat label="Zonas con vacancia (AGEB)" value={stats.counts.vacancy_heatmap ?? 0} compact />
            <Stat label="Edificaciones mapeadas" value={stats.counts.buildings ?? 0} compact />
          </div>
        ) : (
          <div className="empty">cargando…</div>
        )}
        {matchedTotal !== null && stats && matchedTotal !== totalProps && (
          <div className="filter-hint">
            <strong>{matchedTotal.toLocaleString('es-MX')}</strong> con filtros actuales
          </div>
        )}
      </section>

      {stats?.bySource && stats.bySource.length > 0 && (
        <section className="section">
          <div className="section-title">Cobertura por fuente</div>
          <SourceBreakdown sources={stats.bySource} totalProps={totalProps} />
        </section>
      )}

      {((stats?.byEstado && stats.byEstado.length > 0) ||
        (stats?.priceHistogram && stats.priceHistogram.some((b) => b.count > 0))) && (
        <section className="section">
          <div className="section-title">Distribución</div>
          {stats?.byEstado && stats.byEstado.length > 0 && (
            <EstadoDistribution items={stats.byEstado} />
          )}
          {stats?.priceHistogram && stats.priceHistogram.some((b) => b.count > 0) && (
            <PriceHistogram buckets={stats.priceHistogram} />
          )}
        </section>
      )}

      <section className="section">
        <div className="section-title">Fuentes · se actualizan cada 24 h</div>
        <HealthDashboard />
      </section>

      {stats?.territorial && stats.territorial.length > 0 && (
        <section className="section">
          <div className="section-title">Datos territoriales</div>
          <TerritorialList items={stats.territorial} />
        </section>
      )}

      <section className="section">
        <div className="section-title">Acerca de</div>
        <p style={{ fontSize: 12, color: 'var(--text-dim)', lineHeight: 1.5, margin: 0 }}>
          Mapa en vivo de propiedades distressed (BBVA, Banorte, Banregio,
          INDEP, Pendulum REO multi-cartera y HSBC México) sobre un mapa de
          calor de vacancia compuesta por AGEB INEGI, enriquecido con
          incidencia delictiva municipal del SESNSP, riesgos CENAPRED y
          apreciación SHF. Datos refrescan cada 24 h.{' '}
          <a href={api.base + '/v1/stats'} target="_blank" rel="noopener" style={{ color: 'var(--accent)' }}>Estadísticas agregadas</a>.
        </p>
      </section>
    </aside>
  );
}

function Stat({ label, value, compact }: { label: string; value: number; compact?: boolean }) {
  const formatted = compact && value >= 1000
    ? value >= 1_000_000
      ? (value / 1_000_000).toFixed(1) + 'M'
      : (value / 1000).toFixed(1) + 'K'
    : value.toLocaleString('es-MX');
  return (
    <div className="stat">
      <div className="stat-value">{formatted}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

/**
 * Mapa de display name por sourceType. Centralizado acá por simplicidad
 * (tres componentes lo necesitan); cuando crezca la lista conviene
 * extraer a `lib/sourceLabels.ts`.
 */
const SOURCE_LABELS: Record<string, string> = {
  BBVA_REO: 'BBVA Inmuebles',
  BANORTE_REO: 'Banorte Inmuebles',
  BANREGIO_REO: 'Banregio Inmuebles',
  INDEP_SUBASTA: 'INDEP (ex-SAE)',
  SHF_PENDULUM: 'Pendulum REO (multi-cartera SHF)',
  HSBC_REO: 'HSBC México adjudicados',
  SESNSP_INCIDENCIA: 'SESNSP incidencia delictiva',
  CENAPRED_ATLAS: 'CENAPRED Atlas de riesgos',
  SHF_INDEX: 'SHF índice de precios (apreciación)',
  INEGI_INDICADORES: 'INEGI indicadores',
  INEGI_INV: 'INEGI inventario vivienda',
};

function labelFor(sourceType: string): string {
  return SOURCE_LABELS[sourceType] ?? sourceType;
}

function SourceBreakdown({ sources, totalProps }: { sources: StatsBySource[]; totalProps: number }) {
  // Ordenamos por properties desc; el backend ya lo hace pero lo
  // garantizamos también acá por si el cliente recibe una respuesta
  // cacheada con orden distinto.
  const sorted = [...sources].sort((a, b) => b.properties - a.properties);
  return (
    <div className="source-breakdown">
      {sorted.map((s) => (
        <SourceRow key={s.sourceType} src={s} totalProps={totalProps} />
      ))}
    </div>
  );
}

function SourceRow({ src, totalProps }: { src: StatsBySource; totalProps: number }) {
  const pct = totalProps > 0 ? (src.properties / totalProps) * 100 : 0;
  const lastSuccess = src.lastSuccessAt ? new Date(src.lastSuccessAt) : null;
  const ageHours = lastSuccess
    ? (Date.now() - lastSuccess.getTime()) / (1000 * 60 * 60)
    : null;
  const fresh = ageHours !== null && ageHours <= 30;
  return (
    <div className="src-row">
      <div className="src-row-head">
        <span className="src-name" title={src.sourceType}>{labelFor(src.sourceType)}</span>
        <span className="src-count">{src.properties.toLocaleString('es-MX')}</span>
      </div>
      <div className="src-bar">
        <div className="src-bar-fill" style={{ width: `${Math.max(2, pct)}%` }} />
      </div>
      <div className="src-meta">
        <span className={`src-dot ${fresh ? 'fresh' : 'stale'}`} title={lastSuccess ? `última corrida: ${lastSuccess.toLocaleString('es-MX')}` : 'sin corridas'} />
        <span>
          {pct.toFixed(1)}% del total
          {src.recordsNew !== null && src.recordsNew > 0
            ? ` · +${src.recordsNew} nuevas última corrida`
            : ''}
        </span>
      </div>
    </div>
  );
}

/**
 * Mini-chart de barras horizontales: propiedades por estado (top 8). Las
 * barras se escalan al máximo del set para que la #1 llene el ancho. El
 * resto de estados se colapsa en "+N estados" para no alargar el panel.
 */
function EstadoDistribution({ items }: { items: StatsByEstado[] }) {
  const TOP = 8;
  const sorted = [...items].sort((a, b) => b.count - a.count);
  const top = sorted.slice(0, TOP);
  const rest = sorted.slice(TOP);
  const max = top[0]?.count ?? 1;
  const restTotal = rest.reduce((s, e) => s + e.count, 0);
  return (
    <div className="dist-block">
      <div className="dist-caption">Propiedades por estado</div>
      <div className="dist-chart">
        {top.map((e) => (
          <div key={e.cveEnt} className="dist-row" title={`${ESTADO_NAMES[e.cveEnt] ?? e.cveEnt}: ${e.count.toLocaleString('es-MX')}`}>
            <span className="dist-label">{ESTADO_NAMES[e.cveEnt] ?? e.cveEnt}</span>
            <span className="dist-bar">
              <span className="dist-bar-fill" style={{ width: `${Math.max(3, (e.count / max) * 100)}%` }} />
            </span>
            <span className="dist-count">{e.count.toLocaleString('es-MX')}</span>
          </div>
        ))}
      </div>
      {rest.length > 0 && (
        <div className="dist-foot">+{rest.length} estados más · {restTotal.toLocaleString('es-MX')} propiedades</div>
      )}
    </div>
  );
}

/**
 * Mini-chart de barras verticales: distribución de precios por bucket fijo
 * MXN. Útil para que el usuario vea de un vistazo el rango dominante del
 * inventario (la mayoría suele caer en $500k–2M).
 */
function PriceHistogram({ buckets }: { buckets: StatsPriceBucket[] }) {
  const max = buckets.reduce((m, b) => Math.max(m, b.count), 0) || 1;
  const total = buckets.reduce((s, b) => s + b.count, 0);
  return (
    <div className="dist-block">
      <div className="dist-caption">Precio de venta ({total.toLocaleString('es-MX')} con precio)</div>
      <div className="hist-chart">
        {buckets.map((b) => {
          const pct = total > 0 ? (b.count / total) * 100 : 0;
          return (
            <div
              key={b.label}
              className="hist-col"
              title={`${b.label}: ${b.count.toLocaleString('es-MX')} (${pct.toFixed(0)}%)`}
            >
              <div className="hist-bar-wrap">
                <div className="hist-bar" style={{ height: `${Math.max(4, (b.count / max) * 100)}%` }} />
              </div>
              <div className="hist-xlabel">{b.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TerritorialList({ items }: { items: StatsTerritorial[] }) {
  return (
    <div className="territorial-list">
      {items.map((t) => {
        const lastLoad = t.lastLoadedAt ? new Date(t.lastLoadedAt) : null;
        const periodLabel = t.periodText ?? formatPeriod(t.latestPeriod);
        return (
          <div key={t.sourceType} className="terr-row">
            <div className="terr-head">
              <span className="terr-name">{labelFor(t.sourceType)}</span>
              <span className="terr-rows">{t.rows.toLocaleString('es-MX')} filas</span>
            </div>
            <div className="terr-meta">
              <span><strong>{t.municipios.toLocaleString('es-MX')}</strong> municipios cubiertos</span>
              {periodLabel && <span> · periodo más reciente: <strong>{periodLabel}</strong></span>}
              {lastLoad && <span> · cargado {relTime(lastLoad)}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function formatPeriod(period: string | null): string | null {
  if (!period || !/^\d{6}$/.test(period)) return null;
  const year = period.slice(0, 4);
  const monthNum = Number(period.slice(4));
  const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  if (monthNum < 1 || monthNum > 12) return null;
  return `${months[monthNum - 1]} ${year}`;
}

function relTime(d: Date): string {
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return 'hace segundos';
  if (sec < 3600) return `hace ${Math.floor(sec / 60)} min`;
  if (sec < 86400) return `hace ${Math.floor(sec / 3600)} h`;
  const days = Math.floor(sec / 86400);
  if (days < 30) return `hace ${days} d`;
  const months = Math.floor(days / 30);
  return `hace ${months} mes${months === 1 ? '' : 'es'}`;
}
